/**
 * Azure Text-to-Speech Avatar Hook
 * Manages WebRTC connection for real-time avatar video synthesis
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import { applyPronunciation } from '../utils/pronunciation-helper';
import * as SpeechSDK from 'microsoft-cognitiveservices-speech-sdk';

// ================================
// Status Model
// ================================

/**
 * Connection / synthesis lifecycle states exposed by the hook.
 *
 * - `IDLE`: Not connected yet.
 * - `INITIALIZING`: Fetching config, creating SDK objects, and negotiating WebRTC.
 * - `CONNECTED`: Avatar session active and ready to speak.
 * - `SPEAKING`: Currently synthesizing and streaming speech.
 * - `ERROR`: A recoverable or terminal error occurred.
 * - `DISCONNECTED`: Explicitly disconnected / cleaned up.
 */
export const AvatarStatus = {
  /** Not connected yet. */
  IDLE: 'IDLE',
  /** Fetching config, creating SDK objects, and negotiating WebRTC. */
  INITIALIZING: 'INITIALIZING',
  /** Avatar session active and ready to speak. */
  CONNECTED: 'CONNECTED',
  /** Currently synthesizing and streaming speech. */
  SPEAKING: 'SPEAKING',
  /** A recoverable or terminal error occurred. */
  ERROR: 'ERROR',
  /** Explicitly disconnected / cleaned up. */
  DISCONNECTED: 'DISCONNECTED',
} as const;

export type AvatarStatus = (typeof AvatarStatus)[keyof typeof AvatarStatus];

interface UseAvatarSynthesizerProps {
  /**
   * Callback fired whenever `status` changes.
   */
  onStatusChange?: (status: AvatarStatus) => void;

  /**
   * Callback fired when any hook-level error is raised.
   */
  onError?: (error: string) => void;

  /**
   * Avatar configuration
   *
   * Defaults are applied for all values when omitted.
   */
  avatarCharacter?: string;
  avatarStyle?: string;
  ttsVoice?: string;
  backgroundColor?: string;
  isPhoto?: boolean;
}

/**
 * Avatar synthesis result determination logic
 */
interface AvatarSynthesizerState {
  /** Current status in the avatar lifecycle. */
  status: AvatarStatus;
  /** Last error message, if any. */
  error: string | null;
  /** Whether speech synthesis is actively in progress. */
  isSpeaking: boolean;
}

/**
 * Hook that encapsulates Azure Avatar TTS + WebRTC setup, speech queueing,
 * playback control, and full cleanup.
 */
export function useAvatarSynthesizer({
  onStatusChange,
  onError,
  avatarCharacter = 'lisa',
  avatarStyle = 'casual-sitting',
  ttsVoice = 'en-US-JennyNeural',
  isPhoto = false,
}: UseAvatarSynthesizerProps = {}) {
  // --------------------------------
  // React state
  // --------------------------------

  const [state, setState] = useState<AvatarSynthesizerState>({
    status: AvatarStatus.IDLE,
    error: null,
    isSpeaking: false,
  });

  // --------------------------------
  // Mutable refs (do not trigger renders)
  // --------------------------------

  /** Active Azure avatar synthesizer instance. */
  const avatarSynthesizerRef = useRef<SpeechSDK.AvatarSynthesizer | null>(null);

  /** Active peer connection used for audio/video transport. */
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);

  /** FIFO queue for speech requests arriving while avatar is already speaking. */
  const spokenTextQueueRef = useRef<string[]>([]);

  /** Video element sink that receives the combined media stream. */
  const videoElementRef = useRef<HTMLVideoElement | null>(null);

  /** Fast, render-independent speaking flag used for queue control. */
  const isSpeakingRef = useRef<boolean>(false);

  /** Combined media stream that aggregates incoming audio + video tracks. */
  const mediaStreamRef = useRef<MediaStream | null>(null);

  // --------------------------------
  // Local utilities
  // --------------------------------

  /**
   * Sets the lifecycle status in state and notifies external listener.
   */
  const updateStatus = useCallback(
    (status: AvatarStatus) => {
      setState((prev) => ({ ...prev, status }));
      onStatusChange?.(status);
    },
    [onStatusChange],
  );

  /**
   * Centralized error handler for consistent logging/state/callback behavior.
   */
  const handleError = useCallback(
    (error: string) => {
      console.error('[Avatar]', error);
      setState((prev) => ({ ...prev, error, status: AvatarStatus.ERROR }));
      onError?.(error);
    },
    [onError],
  );

  /**
   * Fetch ICE server credentials from Azure
   *
   * The relay token endpoint returns a mixed list of STUN/TURN URLs.
   * This hook intentionally filters to TURN URLs for relay reliability in
   * restrictive network conditions.
   */
  const fetchIceToken = useCallback(
    async (config: { endpoint: string; key: string }): Promise<RTCIceServer> => {
      const response = await fetch(
        `${config.endpoint}/tts/cognitiveservices/avatar/relay/token/v1`,
        {
          method: 'GET',
          headers: {
            'Ocp-Apim-Subscription-Key': config.key,
          },
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch ICE token: ${response.statusText}`);
      }

      const data = await response.json();
      // Filter for TURN URLs only
      const turnUrls = data.Urls.filter((url: string) => url.startsWith('turn'));

      // Return RTC-compatible ICE server config.
      return {
        urls: turnUrls,
        username: data.Username,
        credential: data.Password,
      };
    },
    [],
  );

  /**
   * Initialize avatar synthesizer and WebRTC connection
   *
   * Steps:
   * 1. Fetch TTS service config from backend.
   * 2. Fetch ICE credentials.
   * 3. Build Speech SDK config + avatar config.
   * 4. Create synthesizer and peer connection.
   * 5. Wire media tracks to provided video element.
   * 6. Start avatar session and transition to CONNECTED.
   */
  const connect = useCallback(
    async (videoElement: HTMLVideoElement) => {
      try {
        // Mark init state immediately for UI responsiveness.
        updateStatus(AvatarStatus.INITIALIZING);
        videoElementRef.current = videoElement;

        // Fetch TTS config from backend
        const backendUrl = process.env.NEXT_PUBLIC_VITE_BACKEND_URL;
        const configResponse = await fetch(`${backendUrl}/speech/api/speech-config-tts`);
        console.log('Fetching TTS config from:', `${backendUrl}/speech/api/speech-config-tts`);
        if (!configResponse.ok) {
          throw new Error('Failed to fetch TTS config');
        }
        const ttsConfig = await configResponse.json();

        // Fetch ICE server credentials
        const iceServer = await fetchIceToken(ttsConfig);

        // Extract hostname from endpoint for fromHost
        const endpointUrl = new URL(ttsConfig.endpoint);
        const hostname = endpointUrl.hostname;

        // Create speech synthesis config with private endpoint
        const speechConfig = SpeechSDK.SpeechConfig.fromHost(
          new URL(`wss://${hostname}`),
          ttsConfig.key,
        );

        // Apply selected neural voice for speech output.
        speechConfig.speechSynthesisVoiceName = ttsVoice;

        // Create avatar config with proper video format
        const videoFormat = new SpeechSDK.AvatarVideoFormat();
        // videoFormat.setCropRange(
        //   new SpeechSDK.Coordinate(600, 0),
        //   new SpeechSDK.Coordinate(1320, 1080)
        // );

        let avatarConfig;

        if (isPhoto) {
          // For custom photo avatars
          avatarConfig = new SpeechSDK.AvatarConfig(avatarCharacter, avatarStyle, videoFormat);
          avatarConfig.photoAvatarBaseModel = 'vasa-1';
          avatarConfig.customized = true;
        } else {
          // For video avatars
          avatarConfig = new SpeechSDK.AvatarConfig(avatarCharacter, avatarStyle, videoFormat);
        }
        avatarConfig.backgroundImage = new URL('https://i.ibb.co/yFZBx45x/1764606401646.png');

        // Create avatar synthesizer
        const synthesizer = new SpeechSDK.AvatarSynthesizer(speechConfig, avatarConfig);
        avatarSynthesizerRef.current = synthesizer;

        // Set up avatar event handlers
        // Placeholder hook point: intentionally no-op for now.
        synthesizer.avatarEventReceived = () => {
          // Avatar events received
        };

        // Set up WebRTC peer connection
        const peerConnection = new RTCPeerConnection({
          iceServers: [iceServer],
        });
        peerConnectionRef.current = peerConnection;

        // Handle ICE connection state changes
        peerConnection.oniceconnectionstatechange = () => {
          if (peerConnection.iceConnectionState === 'failed') {
            handleError('WebRTC ICE connection failed');
          }
        };

        // Handle incoming video and audio streams
        peerConnection.ontrack = (event) => {
          if (!videoElementRef.current) {
            console.error('[WebRTC] videoElementRef.current is null!');
            return;
          }

          // Create or get the MediaStream to combine audio and video
          if (!mediaStreamRef.current) {
            mediaStreamRef.current = new MediaStream();
          }

          // Add the track to our combined MediaStream
          mediaStreamRef.current.addTrack(event.track);

          // Set/update the video element's source with the combined stream
          videoElementRef.current.srcObject = mediaStreamRef.current;
          videoElementRef.current.autoplay = true;
          videoElementRef.current.playsInline = true;
          videoElementRef.current.muted = false;
          videoElementRef.current.volume = 1.0;

          // Force play to handle autoplay restrictions
          videoElementRef.current.play().catch((err) => {
            console.error('[Video] Error playing video:', err);
          });

          // Set up event handlers once (only on first track)
          if (event.track.kind === 'video') {
            videoElementRef.current.onerror = (e) => {
              console.error('[Video] Error:', e);
            };
          }
        };

        // Add transceivers for audio and video
        peerConnection.addTransceiver('video', { direction: 'sendrecv' });
        peerConnection.addTransceiver('audio', { direction: 'sendrecv' });

        // Start avatar and establish WebRTC connection
        const result = await synthesizer.startAvatarAsync(peerConnection);

        if (result.reason === SpeechSDK.ResultReason.SynthesizingAudioCompleted) {
          updateStatus(AvatarStatus.CONNECTED);
        } else {
          let errorMessage = 'Failed to start avatar';
          if (result.reason === SpeechSDK.ResultReason.Canceled) {
            const cancellation = SpeechSDK.CancellationDetails.fromResult(
              result as unknown as SpeechSDK.SpeechSynthesisResult,
            );
            if (cancellation.reason === SpeechSDK.CancellationReason.Error) {
              errorMessage = cancellation.errorDetails;
            }
          }
          throw new Error(errorMessage);
        }
      } catch (error) {
        // Normalize unknown throw values into a safe string message.
        handleError(error instanceof Error ? error.message : String(error));
      }
    },
    [avatarCharacter, avatarStyle, isPhoto, ttsVoice, fetchIceToken, updateStatus, handleError],
  );

  /**
   * Speak a sentence with the avatar
   *
   * Behavior:
   * - If currently speaking, enqueue text and return immediately.
   * - Otherwise synthesize now, then drain queue recursively.
   * - Any synthesis error transitions hook state to ERROR.
   */
  const speak = useCallback(
    async (text: string) => {
      const synthesizer = avatarSynthesizerRef.current;
      if (!synthesizer) {
        handleError('Avatar not connected');
        return;
      }

      if (isSpeakingRef.current) {
        // Queue the text if already speaking
        spokenTextQueueRef.current.push(text);
        return;
      }

      try {
        isSpeakingRef.current = true;
        setState((prev) => ({ ...prev, isSpeaking: true }));
        updateStatus(AvatarStatus.SPEAKING);

        // Apply pronunciation transformations (includes XML escaping and phoneme tags)
        const textWithPronunciation = applyPronunciation(text);

        // Create SSML with no leading silence for low latency
        const ssml = `
<speak version='1.0' xmlns='http://www.w3.org/2001/10/synthesis' xmlns:mstts='http://www.w3.org/2001/mstts' xml:lang='en-US'>
  <voice name='${ttsVoice}'>
    <mstts:leadingsilence-exact value='0'/>
    ${textWithPronunciation}
  </voice>
</speak>`.trim();
        const result = await synthesizer.speakSsmlAsync(ssml);

        if (result.reason === SpeechSDK.ResultReason.SynthesizingAudioCompleted) {
          // Speech completed successfully
        } else if (result.reason === SpeechSDK.ResultReason.Canceled) {
          const cancellation = SpeechSDK.CancellationDetails.fromResult(
            result as unknown as SpeechSDK.SpeechSynthesisResult,
          );
          if (cancellation.reason === SpeechSDK.CancellationReason.Error) {
            throw new Error(cancellation.errorDetails);
          }
        }

        // Process queue
        const nextText = spokenTextQueueRef.current.shift();
        if (nextText) {
          // Reset speaking flag before recursive call to avoid re-queuing
          isSpeakingRef.current = false;
          setState((prev) => ({ ...prev, isSpeaking: false }));
          // Recursively speak next item
          speak(nextText);
        } else {
          isSpeakingRef.current = false;
          setState((prev) => ({ ...prev, isSpeaking: false }));

          // Return to ready state once queue is empty.
          updateStatus(AvatarStatus.CONNECTED);
        }
      } catch (error) {
        isSpeakingRef.current = false;
        setState((prev) => ({ ...prev, isSpeaking: false }));
        handleError(error instanceof Error ? error.message : String(error));
      }
    },
    [ttsVoice, updateStatus, handleError],
  );

  /**
   * Stop current speech
   *
   * Clears pending queue and asks SDK to stop active utterance.
   */
  const stopSpeaking = useCallback(async () => {
    const synthesizer = avatarSynthesizerRef.current;
    if (!synthesizer) return;

    try {
      // Clear queue
      spokenTextQueueRef.current = [];

      await synthesizer.stopSpeakingAsync();
      setState((prev) => ({ ...prev, isSpeaking: false }));
      updateStatus(AvatarStatus.CONNECTED);
    } catch (error) {
      handleError(error instanceof Error ? error.message : String(error));
    }
  }, [updateStatus, handleError]);

  /**
   * Disconnect and cleanup
   *
   * Safely closes synthesizer + peer connection, releases media bindings,
   * clears queue, and transitions hook to DISCONNECTED.
   */
  const disconnect = useCallback(() => {
    if (avatarSynthesizerRef.current) {
      avatarSynthesizerRef.current.close();
      avatarSynthesizerRef.current = null;
    }

    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }

    if (videoElementRef.current) {
      videoElementRef.current.srcObject = null;
    }

    // Clear the MediaStream ref
    mediaStreamRef.current = null;

    spokenTextQueueRef.current = [];
    setState({
      status: AvatarStatus.DISCONNECTED,
      error: null,
      isSpeaking: false,
    });
    updateStatus(AvatarStatus.DISCONNECTED);
  }, [updateStatus]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  // Public API surface consumed by UI components.
  return {
    ...state,
    connect,
    speak,
    stopSpeaking,
    disconnect,
    isConnected: state.status === AvatarStatus.CONNECTED || state.status === AvatarStatus.SPEAKING,
  };
}
